import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, RemoveFormatting } from "lucide-react";

// Small allow-list mirrors the backend bleach config.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "hr", "div", "span",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "strong", "b", "em", "i", "u", "s", "strike",
    "sub", "sup", "small", "mark",
    "ul", "ol", "li",
    "blockquote", "pre", "code",
    "a",
  ],
  ALLOWED_ATTR: ["href", "title", "target", "rel", "style", "class"],
  ALLOW_DATA_ATTR: false,
};

const sanitize = (html) => DOMPurify.sanitize(html || "", PURIFY_CONFIG);

/**
 * Minimal paste-preserving rich text editor.
 * - Native contentEditable + document.execCommand (Bold, Italic, insertUnorderedList, insertOrderedList).
 * - Paste handler intercepts, sanitizes, and inserts as HTML (preserves headings/bullets/bold/links from Word/GDocs).
 * - No external rich-text framework.
 */
export default function RichTextEditor({ value, onChange, "data-testid": testid = "rich-editor" }) {
  const ref = useRef(null);
  const initialized = useRef(false);

  // Initialize innerHTML once (avoid caret jumps on every keystroke)
  useEffect(() => {
    if (!ref.current) return;
    if (!initialized.current) {
      ref.current.innerHTML = sanitize(value || "");
      initialized.current = true;
    }
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    onChange?.(sanitize(ref.current.innerHTML));
  };

  const cmd = (command, arg = null) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    emit();
  };

  const onPaste = (e) => {
    e.preventDefault();
    const cd = e.clipboardData;
    // Prefer HTML paste; if only plain text, fall back to text
    const html = cd.getData("text/html");
    const text = cd.getData("text/plain");
    const dirty = html || text.replace(/\n/g, "<br>");
    const clean = sanitize(dirty);
    document.execCommand("insertHTML", false, clean);
    emit();
  };

  const insertLink = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      alert("Select some text first, then add a link.");
      return;
    }
    const url = window.prompt("Link URL (https://…):", "https://");
    if (!url) return;
    try { new URL(url); } catch { alert("That URL doesn't look right."); return; }
    cmd("createLink", url);
  };

  const clearFormat = () => cmd("removeFormat");

  const Btn = ({ onMouseDown, title, children, ...rest }) => (
    <button type="button" title={title}
      onMouseDown={(e) => { e.preventDefault(); onMouseDown(); }}
      className="px-2 py-1 border border-brass/40 text-brass hover:text-gold hover:border-gold"
      {...rest}>
      {children}
    </button>
  );

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-toolbar`}>
        <Btn onMouseDown={() => cmd("bold")} title="Bold" data-testid="rt-bold"><Bold size={14} /></Btn>
        <Btn onMouseDown={() => cmd("italic")} title="Italic" data-testid="rt-italic"><Italic size={14} /></Btn>
        <Btn onMouseDown={() => cmd("underline")} title="Underline" data-testid="rt-underline"><Underline size={14} /></Btn>
        <Btn onMouseDown={() => cmd("insertUnorderedList")} title="Bullet list" data-testid="rt-ul"><List size={14} /></Btn>
        <Btn onMouseDown={() => cmd("insertOrderedList")} title="Numbered list" data-testid="rt-ol"><ListOrdered size={14} /></Btn>
        <Btn onMouseDown={insertLink} title="Add link" data-testid="rt-link"><LinkIcon size={14} /></Btn>
        <select onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) cmd("formatBlock", v); }}
          className="text-xs bg-transparent border border-brass/40 text-brass px-2 py-1"
          data-testid="rt-block">
          <option value="">Style…</option>
          <option value="H2">Heading</option>
          <option value="H3">Subheading</option>
          <option value="P">Paragraph</option>
          <option value="BLOCKQUOTE">Quote</option>
        </select>
        <Btn onMouseDown={clearFormat} title="Clear formatting" data-testid="rt-clear"><RemoveFormatting size={14} /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        data-testid={`${testid}-editable`}
        className="input-vintage min-h-[240px] max-h-[60vh] overflow-y-auto font-serif prose-page whitespace-pre-wrap focus:outline-none"
        style={{ lineHeight: 1.65 }}
      />
      <p className="text-[10px] text-parchment/50">
        Paste from Word or Google Docs — bold, italics, headings, bullets and links are preserved. Scripts and unsafe HTML are stripped.
      </p>
    </div>
  );
}
